import { useCallback, useMemo, useState } from 'react';
import { Alert, Box, Button, CircularProgress, Stack, Typography } from '@mui/material';
import useGetPostedInvoices from '../hooks/useGetPostedInvoices';
import useLocalStorageState from '../../utils/useLocalStorageState';
import { COLORS } from '../../utils/Application_Constants';
import useInvoicingFilters from '../hooks/Invoicing/useFilters';
import FilterDrawer from '../../Shared/Components/FilterDrawer/FilterDrawer';
import SearchInputWithDropdown from '../../Shared/Components/SearchInputWithDropdown';
import { managementSearchOptions } from '../Configurations/invoicingConfig';
import { pluralize } from '../../utils/helpers';
import { formatFiltersForApi } from '../../utils/filterUtils';
import PageContainer from '../Components/PageContainer';
import Tabs from '../../Shared/Components/Tab/Tabs';
import Tab from '../../Shared/Components/Tab/Tab';
import TabPanel from '../../Shared/Components/Tab/TabPanel';
import { InvoicingSelectionContextProvider } from '../Contexts/InvoicingSelectionContext';
import ListViewTableHeaderRow from '../../Shared/Components/ListViewTable/ListViewTableHeaderRow';
import BookingItemContainer from '../Components/Invoicing/InvoiceManagement/BookingItemContainer';
import PaginationBar from '../../Shared/Components/PaginationBar';
import { MIN_COLUMN_WIDTH } from '../Components/Invoicing/styles';

const PAGE_NAME = 'invoiceManagement';
const minWidth = MIN_COLUMN_WIDTH;
const TABLE_HEADERS = [
    { label: 'Client', key: 'client', size: 1, sx: { minWidth } },
    {
        label: (
            <>
                Customer
                <br />
                Number
            </>
        ),
        title: 'Customer Number',
        key: 'customerNumber',
        size: 1,
        sx: { minWidth },
    },
    { label: 'Invoice #', key: 'invoiceNumber', size: 1, sx: { minWidth } },
    { label: 'Clinician', key: 'clinician', size: 1, sx: { minWidth } },
    { label: 'Booking', key: 'bookingId', size: 1, sx: { minWidth } },
    {
        label: (
            <>
                Invoice
                <br />
                Date
            </>
        ),
        title: 'Invoice Date',
        key: 'invoiceDate',
        size: 1,
        sx: { minWidth },
    },
    {
        label: (
            <>
                Posted
                <br />
                Date
            </>
        ),
        title: 'Posted Date',
        key: 'postedDate',
        size: 1,
        sx: { minWidth },
    },
    { label: 'Status', key: 'status', size: 1, sx: { minWidth: 130 } },
    { label: 'Amount', key: 'amount', size: 1, sx: { minWidth } },
    { label: 'Type', key: 'type', size: 1, sx: { minWidth } },
    { label: 'Actions', key: 'actions', size: 0.5 },
];

function InvoiceManagementPage() {
    const { state: sortBy, setValue: setSortBy } = useLocalStorageState(`${PAGE_NAME}SortField`);
    const { state: sortDirection, setValue: setSortDirection } = useLocalStorageState(
        `${PAGE_NAME}SortDirection`,
        'asc'
    );
    const { state: searchData, setValue: setSearch } = useLocalStorageState(`${PAGE_NAME}Search`, {
        searchWord: '',
        searchColumns: '',
    });
    const { state: tabValue, setValue: setTabValue } = useLocalStorageState(
        `${PAGE_NAME}SelectedTab`,
        'pre-invoiced'
    );
    const {
        filters,
        handleApplyFilter,
        filtersSetup,
        isLoadingFilterConfig,
        appliedFiltersDescription,
    } = useInvoicingFilters(PAGE_NAME);

    const [page, setPage] = useState(1);
    const handlePageChange = (_, newPage) => {
        setPage(newPage);
    };

    const requestParams = useMemo(
        () => ({
            pageSize: 20,
            invoiceStatus: tabValue,
            ...formatFiltersForApi(filters, filtersSetup),
            ...(sortBy ? { orderBy: sortBy, dir: sortDirection } : {}),
            ...(searchData?.searchWord ? searchData : {}),
        }),
        [tabValue, sortBy, sortDirection, filters, filtersSetup, searchData]
    );

    const { data: bookings, isLoading, isFetching } = useGetPostedInvoices(requestParams);

    const totalCount = bookings?.pages[0]?.totalCount || 0;
    // TODO: replace this when we wire up the infite scroll and are using pages
    const bookingsData = bookings?.pages[0].data;

    const handleSortChange = useCallback((key, direction) => {
        setSortBy(key);
        setSortDirection(direction);
    }, []);

    const pageSize = bookings?.pages?.[0]?.pageSize || 20;
    const totalPages = Math.ceil(totalCount / pageSize);

    return (
        <PageContainer>
            <Typography variant="h3" sx={{ fontSize: 22 }}>
                Invoice Management
            </Typography>

            <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                spacing={1}
                mt="12px"
            >
                <FilterDrawer
                    isLoading={isLoadingFilterConfig || isLoading}
                    config={filtersSetup}
                    onChange={handleApplyFilter}
                    storedValues={filters}
                />

                <SearchInputWithDropdown
                    isLoading={isLoading}
                    options={managementSearchOptions}
                    initialSearch={searchData?.searchWord}
                    initialSearchKey={searchData?.searchColumns}
                    onSubmit={({ search, searchKey }) =>
                        setSearch({ searchWord: search, searchColumns: searchKey })
                    }
                />
            </Stack>

            <Typography sx={{ my: 2, fontSize: 12, fontWeight: '400', color: COLORS.LT_MIDNIGHT }}>
                {`${totalCount} ${pluralize(totalCount, 'item')}`}
                {appliedFiltersDescription}
            </Typography>

            <Tabs value={tabValue} onChange={(e, value) => setTabValue(value)}>
                <Tab label="Pre-Invoiced (#)" index={0} value="pre-invoiced" />
                <Tab label="Invoiced (#)" index={1} value="invoiced" />
            </Tabs>
            <TabPanel>
                {!isLoading && bookingsData?.length > 0 ? (
                    <InvoicingSelectionContextProvider bookings={bookingsData}>
                        {({ selectStatus, handleSelectAllBookings, selectedBookingsCount }) => (
                            <Box sx={{ py: '22px', px: '26px', borderRadius: '10px' }}>
                                {tabValue === 'pre-invoiced' && (
                                    <Alert severity="info" sx={{ mb: '15px', fontSize: '12px' }}>
                                        You are viewing pre-invoices. These have not been formally
                                        invoiced to the client.
                                    </Alert>
                                )}

                                <Stack
                                    direction="row"
                                    justifyContent="space-between"
                                    alignItems="center"
                                    mb="20px"
                                >
                                    <Typography color={COLORS.LT_MIDNIGHT}>
                                        {tabValue === 'pre-invoiced' ? 'Pre-Invoices' : 'Invoices'}
                                    </Typography>

                                    {tabValue === 'pre-invoiced' && (
                                        <Stack direction="row" alignItems="center" gap={1}>
                                            <Button
                                                variant="lt-admin-outlined"
                                                color="error"
                                                size="small"
                                                disabled={selectedBookingsCount === 0}
                                            >
                                                Abandon selected ({selectedBookingsCount})
                                            </Button>
                                            <Button
                                                variant="lt-admin-contained"
                                                color="primary"
                                                size="small"
                                                disabled={selectedBookingsCount === 0}
                                            >
                                                Invoice selected ({selectedBookingsCount})
                                            </Button>
                                        </Stack>
                                    )}
                                </Stack>
                                <Box sx={{ width: '100%', overflowX: 'scroll' }}>
                                    <ListViewTableHeaderRow
                                        selectStatus={selectStatus}
                                        onSelectAll={handleSelectAllBookings}
                                        selectColumnProps={{
                                            sx: {
                                                justifyContent: 'flex-end',
                                                minWidth: '90px',
                                                pr: '2.5px !important',
                                            },
                                        }}
                                        columns={TABLE_HEADERS}
                                        onSortChange={handleSortChange}
                                        sortBy={sortBy}
                                        sortDirection={sortDirection}
                                        shouldCleanSort
                                        sortIconPosition="left"
                                    />

                                    {bookingsData?.map((booking) => (
                                        <BookingItemContainer
                                            key={booking.bookingId}
                                            booking={booking}
                                        />
                                    ))}
                                </Box>
                            </Box>
                        )}
                    </InvoicingSelectionContextProvider>
                ) : (
                    <Typography variant="body1" component="p" p={2}>
                        {isLoading || isFetching ? <CircularProgress /> : 'No results found'}
                    </Typography>
                )}
            </TabPanel>

            {bookingsData?.length > 0 && (
                <Box mt="15px">
                    <PaginationBar
                        page={page}
                        totalPages={totalPages}
                        handlePageChange={handlePageChange}
                    />
                </Box>
            )}
        </PageContainer>
    );
}

export default InvoiceManagementPage;
